import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import Components from 'components/common/ComponentIndex';
import { FormElementProps } from 'components/common/form';
import { useFormState } from 'react-hook-form';
import { Container, FormHelperText } from '@mui/material';
import { useSelector } from 'react-redux';
import { FieldErrors } from 'react-hook-form/dist/types/errors';
import { formSliceState } from 'store/formSlice';
import { FormTabType, TabContext } from 'components/common/form/tab';
import useBundleTranslation from 'i18n';
import {
    FormComponentValue,
    FormStaticControlPropsType,
    FormWrapperControlPropsType,
    getFromComponentPropsClass,
} from 'components/common/form/layout/control';
import ComponentDebugInfo from 'components/common/form/element/FormElementDebugInfo';
import ReactHookFormController from 'components/common/form/layout/ReactHookFormController';
import { FormElementWrapper } from 'components/common/form/element/FormElementWrapper';
import FormElementErrorBoundary from 'components/common/form/element/FormElementErrorBoundry';
import type { ContainerMaxWidthType } from 'components/common/form/formTools';
import CollapseText from 'components/common/collapseText/CollapseText';
import FormElementErrorMessage from 'components/common/form/element/FormElementErrorMessage';
import useFormDependency from 'components/common/form/element/useFormDependency';
import { prepareTextDataObject } from 'tools/tools';

export default function FormElement(props: FormElementProps) {
    // TabContext can hold 'global' props for all components on tab
    const tabContext = useContext<FormTabType | null>(TabContext);
    const { errors } = useFormState({ control: props.form.hookFormControl });
    const formKey = props.form.formKey;

    const [tabWasActive, setTabWasActive] = useState(tabContext?.isActive);
    useEffect(() => {
        if (tabContext?.isActive) {
            setTabWasActive(true);
        }
    }, [tabContext?.isActive]);

    // Get list of allowed values, can be overrided by Deps Rules
    const emptyTmpValues = useRef([]);
    const tmpValues: any = useSelector((state: any) => {
        const form = (state?.form as formSliceState).formElementsValues[formKey];
        if (form && typeof form[props.component.name] != 'undefined') {
            return form[props.component.name];
        }
        return emptyTmpValues.current;
    });
    const [componentStartValues, setComponentStartValues] = useState(tmpValues);
    useEffect(() => {
        if (JSON.stringify(componentStartValues) == JSON.stringify(tmpValues)) {
            return;
        }
        setComponentStartValues(tmpValues);
    }, [tmpValues]);

    const { componentProps, componentValues } = useFormDependency(props, componentStartValues);

    const elementProps = { ...props, componentProps: componentProps };

    // TODO: can affect wrapped component?
    return useMemo(
        () => (
            <FormElementErrorBoundary elementProps={elementProps}>
                <FormElementRender elementProps={elementProps} componentValues={componentValues} errors={errors} />
            </FormElementErrorBoundary>
        ),
        [
            JSON.stringify(elementProps.componentProps),
            JSON.stringify(componentValues),
            JSON.stringify(errors),
            tabWasActive,
            props.form.formDidMount,
        ],
    );
    // return <FormElementRender elementProps={elementProps} componentValues={componentValues} errors={errors} />;
}

function FormElementRender({
    elementProps,
    componentValues,
    errors,
}: {
    elementProps: FormElementProps;
    componentValues: Array<FormComponentValue>;
    errors: FieldErrors<any>;
}) {
    const innerElementProps = { ...elementProps, wrapWithContainer: false };
    const isDebugMode = useSelector((state: any) => state.debug.status);
    const { t } = useBundleTranslation([elementProps?.translationNS ?? 'components/common/form/form']);

    // Static Components
    if (elementProps.component.fieldType == 'static') {
        return (
            <FormContainer elementProps={elementProps} containerMaxWidth={elementProps.containerMaxWidth ?? false}>
                <FormElementWrapper elementProps={innerElementProps}>
                    {React.createElement(
                        Components[elementProps.component.component] as React.FC<FormStaticControlPropsType>,
                        {
                            elementProps: innerElementProps,
                        },
                    )}
                </FormElementWrapper>
            </FormContainer>
        );
    }

    // Wrapper Components
    if (elementProps.component.fieldType == 'wrapper') {
        return (
            <FormContainer elementProps={elementProps} containerMaxWidth={elementProps.containerMaxWidth ?? false}>
                {React.createElement(
                    Components[elementProps.component.component] as React.FC<FormWrapperControlPropsType>,
                    {
                        elementProps: innerElementProps,
                        children: elementProps.component.components?.map((c: any, i: number) => {
                            return <FormElement {...innerElementProps} component={c} key={i} />;
                        }),
                    },
                )}
            </FormContainer>
        );
    }

    const { text: helperTextValue, getFinalHtml: getHelperText } = prepareTextDataObject(
        elementProps?.componentProps?.helperText ?? '',
    );

    // Regular Form Components
    return (
        <FormContainer
            elementProps={elementProps}
            componentValues={componentValues}
            containerMaxWidth={elementProps.containerMaxWidth ?? false}
        >
            <FormElementWrapper elementProps={innerElementProps} componentValues={componentValues}>
                {isDebugMode && <ComponentDebugInfo elementProps={elementProps} />}
                <ReactHookFormController elementProps={innerElementProps} componentValues={componentValues} />
                {helperTextValue && (
                    <>
                        {elementProps.componentProps.helperText && elementProps.componentProps.useCollapseText ? (
                            <CollapseText
                                text={getHelperText(t)}
                                maxLines={elementProps.componentProps?.maxLines}
                            ></CollapseText>
                        ) : (
                            <FormHelperText sx={{ marginLeft: 0 }} className={'markdown-holder'}>
                                {getHelperText(t)}
                            </FormHelperText>
                        )}
                    </>
                )}
                <FormElementErrorMessage errors={errors} elementProps={innerElementProps} t={t} />
            </FormElementWrapper>
        </FormContainer>
    );
}

function FormContainer({
    elementProps,
    containerMaxWidth,
    children,
    componentValues,
}: {
    elementProps: FormElementProps;
    containerMaxWidth: ContainerMaxWidthType;
    children: any;
    componentValues?: Array<FormComponentValue>;
}) {
    const classList = getFromComponentPropsClass(elementProps, componentValues).split(' ');
    const isHiddenField = elementProps?.component?.component === 'FormHidden';
    let className = 'mi-container-3 form-component-holder';
    if (classList.includes('d-none') || isHiddenField) {
        className += ' d-none';
    }
    if (classList.includes('container-remove-indent')) {
        className += '  container-remove-indent';
    }

    const isMaxFullWidth = containerMaxWidth === 'maxFull';
    const maxWidth = isMaxFullWidth ? false : containerMaxWidth;

    if (isMaxFullWidth) className += ' form-container--maxfull';

    if (elementProps?.component?.props?.containerCssClass)
        className += ` ${elementProps.component.props.containerCssClass}`;

    if (elementProps.componentProps?.wrapWithContainer === false) {
        return children;
    }

    return elementProps?.wrapWithContainer ? (
        <Container className={className} maxWidth={maxWidth} component="main" disableGutters={true}>
            {children}
        </Container>
    ) : (
        children
    );
}
